import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { FaxeStore } from '@app/store/faxe/faxe-store-model';

import { onOperationStatus } from '../../client-slices/operation/operation.actions';
import {
  addManyRider,
  fetchAllRider,
  getRider,
  toggleHandled,
  updateToggleOne
} from '@app/store/faxe/rider/rider.actions';
import { selectRidersState } from '@app/store/faxe/rider/rider.selector';
import { push } from '@app/fremework/functions';
import { SliceState } from '@app/store/faxe/sapiens-entity-state';
import { RidersService } from '@app/rest-client/faxe/services/riders.service';
import { notEmptyByRaceSliceState } from '@app/domain/functions';
import { SelectedRaceService } from '@app/services/selected-race.service';

@Injectable()
export class RiderEffects {

  fetchRiderEffect = createEffect(() => this.actions$
    .pipe(
      ofType(fetchAllRider),
      mergeMap(action =>
        this.store.select(selectRidersState())
          .pipe(
            withLatestFrom(of(action)),
            mergeMap(([state, action]) => {

              if (notEmptyByRaceSliceState(
                state.riders,
                action.raceGuid,
                action.requiredSliceState,
                state.sliceState)) {
                return EMPTY;
              }

              const actions = [];
              actions.push(getRider({
                raceGuid: action.raceGuid,
                requiredSliceState: action.requiredSliceState,
                issuerStack: push(action.issuerStack, 'fetchRiderEffect')
              }));
              return of(...actions);
            })
          )
      ),
      catchError(() => EMPTY)
    )
  );

  getRiderEffect = createEffect(() =>
    this.actions$.pipe(
      ofType(getRider),
      exhaustMap(action =>
        this.service.ridersOnlyReportable({raceGuid: action.raceGuid, onlyReportable: action.requiredSliceState !== SliceState.total})
          .pipe( mergeMap((riders) => {
              const actions = [];
              actions.push(addManyRider(
                {
                  models: riders,
                  sliceState: action.requiredSliceState,
                  issuerStack: push(action.issuerStack, 'getRiderEffect')
                }));
              return of(...actions);
            }),
            catchError((e) =>
              of(onOperationStatus({
                  model: {
                    status: e.status,
                    operationId: 'riders',
                    callStack: push(action.issuerStack, 'getRiderEffect')
                  }
                })
              )
            )
          )),
      catchError(() => EMPTY)
    )
  );

  toggleHandledEffect = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleHandled),
      mergeMap((action) =>
        action.isHandled ?
          this.service.setUnhandled({raceGuid: this.selectedRaceService.selectedRaceGuid, riderGuid: action.riderGuid }) :
          this.service.setHandled({raceGuid: this.selectedRaceService.selectedRaceGuid, riderGuid: action.riderGuid })
            .pipe(mergeMap(() => {
                const actions = [];
                actions.push(updateToggleOne(
                  { riderId: action.riderGuid, issuerStack: push(action.issuerStack, 'getRiderEffect') }));
                return of(...actions);
              }),
              catchError((e) =>
                of(onOperationStatus({
                    model: {
                      status: e.status,
                      operationId: 'toggleHandledEffect',
                      callStack: push(action.issuerStack, 'toggleHandledEffect')
                    }
                  })
                )
              )
            )),
      catchError(() => EMPTY)
    )
  );

  constructor(
    private service: RidersService,
    private actions$: Actions,
    private store: Store<FaxeStore>,
    private selectedRaceService: SelectedRaceService
  ) {
  }
}
